import { useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { businessDate, subtractDays, systemClock, type BusinessDate } from '@sakhtar/calendar';
import {
  LoadingState,
  StatePanel,
  V2Badge,
  V2Button,
  V2Card,
  V2CardBody,
  V2CardHeader,
  V2DataGrid,
  V2EmptyState,
  V2FormField,
  V2Kpi,
  type V2GridColumn,
} from '@sakhtar/design-system/react';
import { useApiGet } from '../../lib/useApiGet.js';
import { tenantBasePath } from '../../lib/tenant.js';
import { displayJalaliDate } from '../../lib/date/displayJalali.js';
import { JalaliDateInput } from '../../lib/date/JalaliDateInput.js';
import { formatRialFa } from '../../lib/finance-display.js';
import { useAmountDisplay } from '../../lib/tenantSettings.js';
import type { CardexReport, CardexRow } from '../../lib/types/inventory.js';
import type { MasterDataListResponse } from '../../lib/types/masterData.js';
import {
  MOVEMENT_TYPE_META,
  formatQuantity,
  isTruncated,
  metaOf,
  pickerOptions,
  stateVariantFor,
} from './inventoryDisplay.js';
import './inventory.css';

const PICKER_LIMIT = 200;

/** The opening balance is shown as the sheet's first line, so the grid keeps one row model. */
type CardexLine =
  | { readonly id: string; readonly kind: 'opening' }
  | {
      readonly id: string;
      readonly kind: 'movement';
      readonly index: number;
      readonly row: CardexRow;
    };

const dash = <span>—</span>;

export function CardexPage() {
  const { slug } = useParams<{ slug: string }>();
  const base = slug ? tenantBasePath(slug) : '';
  const { unit, label: amountLabel } = useAmountDisplay();

  // The reorder / stagnant reports link here with `?productId=`.
  const [searchParams] = useSearchParams();
  const [selectedProductId, setSelectedProductId] = useState(searchParams.get('productId') ?? '');
  const [selectedWarehouseId, setSelectedWarehouseId] = useState('');

  const today = systemClock.today();
  const defaultToDate = today;
  const defaultFromDate = subtractDays(businessDate(today), 365);

  const [fromDate, setFromDate] = useState(defaultFromDate);
  const [toDate, setToDate] = useState(defaultToDate);

  const products = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/products?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const warehouses = useApiGet<MasterDataListResponse>(
    slug ? `${base}/api/v1/master-data/warehouses?limit=${PICKER_LIMIT}` : null,
    [slug],
  );
  const productOptions = useMemo(
    () => (products.status === 'ready' ? pickerOptions(products.data.items, 'productId') : []),
    [products],
  );
  const warehouseOptions = useMemo(
    () =>
      warehouses.status === 'ready' ? pickerOptions(warehouses.data.items, 'warehouseId') : [],
    [warehouses],
  );

  const cardexUrl = useMemo(() => {
    if (!slug || !selectedProductId) return null;
    let url = `${base}/api/v1/inventory/reports/cardex?productId=${selectedProductId}&fromDate=${fromDate}&toDate=${toDate}`;
    if (selectedWarehouseId) {
      url += `&warehouseId=${selectedWarehouseId}`;
    }
    return url;
  }, [base, slug, selectedProductId, selectedWarehouseId, fromDate, toDate]);

  const cardexResult = useApiGet<CardexReport>(cardexUrl, [
    selectedProductId,
    selectedWarehouseId,
    fromDate,
    toDate,
  ]);

  function handleQuickRange(months: number) {
    const end = systemClock.today();
    const start = subtractDays(businessDate(end), months * 30);
    setToDate(end);
    setFromDate(start);
  }

  function handleCurrentYear() {
    const end = systemClock.today();
    const start = businessDate(`${end.slice(0, 4)}-01-01`);
    setToDate(end);
    setFromDate(start);
  }

  const report = cardexResult.status === 'ready' ? cardexResult.data : null;

  const lines = useMemo<readonly CardexLine[]>(
    () =>
      report
        ? [
            { id: 'opening', kind: 'opening' },
            ...report.rows.map((row, index) => ({
              id: `${row.movementId}:${index}`,
              kind: 'movement' as const,
              index,
              row,
            })),
          ]
        : [],
    [report],
  );

  const columns: V2GridColumn<CardexLine>[] = report
    ? [
        {
          key: 'index',
          header: 'ردیف',
          align: 'end',
          muted: true,
          hideable: false,
          render: (l) => (l.kind === 'opening' ? dash : (l.index + 1).toLocaleString('fa-IR')),
        },
        {
          key: 'date',
          header: 'تاریخ',
          render: (l) => (
            <span className="sk-num">
              {displayJalaliDate(l.kind === 'opening' ? report.fromDate : l.row.businessDate)}
            </span>
          ),
        },
        {
          key: 'number',
          header: 'شمارهٔ سند',
          render: (l) =>
            l.kind === 'opening' ? (
              <strong>مانده ابتدای دوره</strong>
            ) : (
              <Link
                to={`${base}/inventory/movements/${l.row.movementId}`}
                className="inv-link ltr sk-num"
              >
                {l.row.movementNumber}
              </Link>
            ),
        },
        {
          key: 'type',
          header: 'نوع عملیات',
          render: (l) => {
            if (l.kind === 'opening') return dash;
            const meta = metaOf(MOVEMENT_TYPE_META, l.row.movementType);
            return <V2Badge tone={meta.tone}>{meta.label}</V2Badge>;
          },
        },
        {
          key: 'party',
          header: 'طرف‌حساب / عطف',
          muted: true,
          wrap: true,
          render: (l) =>
            l.kind === 'opening' ? dash : l.row.partyName || l.row.sourceReference || '—',
        },
        {
          key: 'inQty',
          header: 'وارده · تعداد',
          align: 'end',
          emphasis: 'group',
          render: (l) =>
            l.kind === 'opening' || !l.row.inQty ? (
              dash
            ) : (
              <span className="sk-num">{formatQuantity(l.row.inQty)}</span>
            ),
          footer: (
            <span className="sk-num">{report ? formatQuantity(report.totals.totalInQty) : ''}</span>
          ),
        },
        {
          key: 'inUnit',
          header: 'وارده · فی',
          align: 'end',
          emphasis: 'group',
          render: (l) =>
            l.kind === 'opening' || !l.row.inUnitCostRial ? (
              dash
            ) : (
              <span className="sk-num">{formatRialFa(l.row.inUnitCostRial, unit)}</span>
            ),
          footer: dash,
        },
        {
          key: 'inTotal',
          header: 'وارده · مبلغ کل',
          align: 'end',
          emphasis: 'group',
          render: (l) =>
            l.kind === 'opening' || !l.row.inTotalRial ? (
              dash
            ) : (
              <span className="sk-num">{formatRialFa(l.row.inTotalRial, unit)}</span>
            ),
          footer: (
            <span className="sk-num">{formatRialFa(report.totals.totalInValueRial, unit)}</span>
          ),
        },
        {
          key: 'outQty',
          header: 'صادره · تعداد',
          align: 'end',
          emphasis: 'group',
          render: (l) =>
            l.kind === 'opening' || !l.row.outQty ? (
              dash
            ) : (
              <span className="sk-num">{formatQuantity(l.row.outQty)}</span>
            ),
          footer: <span className="sk-num">{formatQuantity(report.totals.totalOutQty)}</span>,
        },
        {
          key: 'outUnit',
          header: 'صادره · فی میانگین',
          align: 'end',
          emphasis: 'group',
          render: (l) =>
            l.kind === 'opening' || !l.row.outUnitCostRial ? (
              dash
            ) : (
              <span className="sk-num">{formatRialFa(l.row.outUnitCostRial, unit)}</span>
            ),
          footer: dash,
        },
        {
          key: 'outTotal',
          header: 'صادره · مبلغ کل',
          align: 'end',
          emphasis: 'group',
          render: (l) =>
            l.kind === 'opening' || !l.row.outTotalRial ? (
              dash
            ) : (
              <span className="sk-num">{formatRialFa(l.row.outTotalRial, unit)}</span>
            ),
          footer: (
            <span className="sk-num">{formatRialFa(report.totals.totalOutValueRial, unit)}</span>
          ),
        },
        {
          key: 'balQty',
          header: 'مانده · موجودی',
          align: 'end',
          emphasis: 'key',
          render: (l) => (
            <span className="sk-num">
              {formatQuantity(
                l.kind === 'opening' ? report.openingBalance.quantity : l.row.balanceQty,
              )}
            </span>
          ),
          footer: <span className="sk-num">{formatQuantity(report.totals.closingQuantity)}</span>,
        },
        {
          key: 'balUnit',
          header: 'مانده · نرخ میانگین',
          align: 'end',
          render: (l) => (
            <span className="sk-num">
              {formatRialFa(
                l.kind === 'opening'
                  ? report.openingBalance.unitCostRial
                  : l.row.balanceUnitCostRial,
                unit,
              )}
            </span>
          ),
          footer: (
            <span className="sk-num">{formatRialFa(report.totals.closingUnitCostRial, unit)}</span>
          ),
        },
        {
          key: 'balTotal',
          header: 'مانده · ارزش کل',
          align: 'end',
          render: (l) => (
            <span className="sk-num">
              {formatRialFa(
                l.kind === 'opening'
                  ? report.openingBalance.totalValueRial
                  : l.row.balanceTotalRial,
                unit,
              )}
            </span>
          ),
          footer: (
            <span className="sk-num">{formatRialFa(report.totals.closingValueRial, unit)}</span>
          ),
        },
      ]
    : [];

  if (!slug) return null;

  return (
    <div className="inv-page">
      {/* Filter Card */}
      <V2Card>
        <V2CardHeader
          icon="search"
          title="کاردکس مقداری و ریالی کالا"
          actions={
            <V2Button
              variant="secondary"
              size="sm"
              icon="invoice"
              onClick={() => window.print()}
              disabled={cardexResult.status !== 'ready'}
            >
              چاپ کاردکس
            </V2Button>
          }
        />
        <V2CardBody>
          <div className="inv-form">
            <div className="inv-filter-grid">
              <V2FormField
                label="کالای مورد نظر"
                required
                width="full"
                hint={
                  products.status === 'ready' &&
                  isTruncated(products.data.items.length, products.data.total)
                    ? `فقط ${PICKER_LIMIT.toLocaleString('fa-IR')} کالای اول فهرست نمایش داده می‌شود.`
                    : undefined
                }
              >
                <select
                  className="sk-select"
                  value={selectedProductId}
                  onChange={(e) => setSelectedProductId(e.target.value)}
                >
                  <option value="">انتخاب کالا…</option>
                  {productOptions.map((p) => (
                    <option key={p.value} value={p.value}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </V2FormField>

              <V2FormField label="انبار (اختیاری)" width="full">
                <select
                  className="sk-select"
                  value={selectedWarehouseId}
                  onChange={(e) => setSelectedWarehouseId(e.target.value)}
                >
                  <option value="">همهٔ انبارها (سرجمع)</option>
                  {warehouseOptions.map((w) => (
                    <option key={w.value} value={w.value}>
                      {w.label}
                    </option>
                  ))}
                </select>
              </V2FormField>

              <V2FormField label="از تاریخ" width="full">
                <JalaliDateInput
                  value={fromDate}
                  onChange={(d) => setFromDate(d as BusinessDate)}
                />
              </V2FormField>

              <V2FormField label="تا تاریخ" width="full">
                <JalaliDateInput value={toDate} onChange={(d) => setToDate(d as BusinessDate)} />
              </V2FormField>
            </div>

            <div className="inv-actions">
              <span className="inv-form__lead">بازهٔ زمانی سریع:</span>
              <V2Button variant="ghost" size="sm" onClick={() => handleQuickRange(1)}>
                ۳۰ روز اخیر
              </V2Button>
              <V2Button variant="ghost" size="sm" onClick={() => handleQuickRange(3)}>
                ۳ ماه اخیر
              </V2Button>
              <V2Button variant="ghost" size="sm" onClick={handleCurrentYear}>
                از ابتدای سال میلادی
              </V2Button>
            </div>
          </div>
        </V2CardBody>
      </V2Card>

      {/* State rendering */}
      {!selectedProductId ? (
        <StatePanel
          variant="empty"
          title="کالای مورد نظر را انتخاب کنید"
          description="برای مشاهدهٔ گردش کاردکس مقداری و ریالی، کالا و بازهٔ زمانی را از فرم بالا مشخص کنید."
        />
      ) : cardexResult.status === 'loading' ? (
        <LoadingState label="در حال محاسبهٔ کاردکس میانگین متحرک کالا…" />
      ) : cardexResult.status === 'error' ? (
        <StatePanel
          variant={stateVariantFor(cardexResult.error.status)}
          title={
            cardexResult.error.status === 403
              ? 'دسترسی مشاهدهٔ کاردکس را ندارید'
              : 'کاردکس قابل نمایش نیست'
          }
        />
      ) : report ? (
        <>
          {/* Summary KPIs */}
          <div className="inv-kpi-grid">
            <V2Kpi
              label="مانده ابتدای دوره"
              value={formatQuantity(report.openingBalance.quantity)}
              delta={`ارزش: ${formatRialFa(report.openingBalance.totalValueRial, unit)} ${amountLabel}`}
            />
            <V2Kpi
              label="جمع وارده در دوره"
              value={formatQuantity(report.totals.totalInQty)}
              delta={`ارزش: ${formatRialFa(report.totals.totalInValueRial, unit)} ${amountLabel}`}
            />
            <V2Kpi
              label="جمع صادره در دوره"
              value={formatQuantity(report.totals.totalOutQty)}
              delta={`ارزش: ${formatRialFa(report.totals.totalOutValueRial, unit)} ${amountLabel}`}
            />
            <div className="inv-kpi-key">
              <V2Kpi
                label="مانده پایان دوره"
                value={formatQuantity(report.totals.closingQuantity)}
                delta={`ارزش کل: ${formatRialFa(report.totals.closingValueRial, unit)} ${amountLabel} · نرخ میانگین: ${formatRialFa(report.totals.closingUnitCostRial, unit)} ${amountLabel}`}
              />
            </div>
          </div>

          {/* Cardex Transactions */}
          <V2DataGrid
            icon="layers"
            title={`گردش کاردکس ${report.productCode} — ${report.productName}`}
            subtitle={`${report.rows.length.toLocaleString('fa-IR')} تراکنش در بازه · مبالغ به ${amountLabel}`}
            columns={columns}
            rows={lines}
            rowKey={(l) => l.id}
            rowTone={(l) => (l.kind === 'opening' ? 'info' : undefined)}
            footerLabel="جمع دوره و مانده پایان"
            storageKey="inventory.cardex"
            zebra
            emptyState={
              <V2EmptyState icon="inventory" title="تراکنشی در این بازه ثبت نشده است" compact />
            }
          />
        </>
      ) : null}
    </div>
  );
}
